import * as React from 'react';
import * as S from './styles';
import * as T from './types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/types';
import { capitalizeFirstLetter, getReviews, getReviewsConfig, saveWidgetsConfig, searchContainedValues } from '../../state/utils';
import { TCButton } from 'components/TCButton';
import { TC_BUTTON_TYPE } from 'components/TCButton/constants';
import Switch from 'components/Switch'
import Chips from 'components/Chips';
import MoreChips from 'features/Dealers/Performance/MoreChips';
import LoopIcon from 'common/icons/loopIcon';
import { Review } from '../../state/types';
import ReviewCard from '../ReviewCard';
import ReviewFilters from '../ReviewFilters';
import { cloneDeep, debounce } from 'lodash';
import DragReviews from '../DragReviews';
import Slider from 'components/Slider';

const MIN_LETTERS_TO_SEARCH = 2;
const CHIPS_NUMBER = 3;
const ATTR_TO_SEARCH = ['detail', 'reviewer'];

const SearchReviews: React.FC<T.IProps> = (props) => {
    const [inputValue, setInputValue] = React.useState("");
    const [isSearching, setIsSearching] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);
    const [openFilters, setOpenFilters] = React.useState(false);
    const [chips, setChips] = React.useState<string[]>([]);
    const [currentFilters, setCurrentFilters] = React.useState<T.ICustomFilter[]>([])
    const [currrentReviews, setCurrentReviews] = React.useState<Review[]>(props.widgetsConfig.topReviews.reviews ?? []);
    const [selectedReviews, setSelectedReviews] = React.useState<Review[]>(props.widgetsConfig.topReviews.reviews ?? []);
    const [automaticScroll, setAutomaticScroll] = React.useState(false);
    const [queryFilters, setQueryFilters] = React.useState<T.IQueryFilters>({});

    const componentRef = React.useRef<HTMLDivElement|null>(null);
    const dispatch = useDispatch();
    const {
        selectedDealerId,
        widgets,
        reviews,
        reviewsConfig,
    } = useSelector((rootState: RootState) => {
        return {
            selectedDealerId: rootState.dealers.dealer.selectedDealer?.id,
            widgets: rootState.reviews.widgets,
            reviews: rootState.reviews.reviews,
            reviewsConfig: rootState.reviews.reviewsConfig,
        }
    }, shallowEqual);

    const shouldNotSave = reviewsConfig && (selectedReviews.length < reviewsConfig.topReviews.minReviews || selectedReviews.length > reviewsConfig.topReviews.maxReviews);
    const [automaticScrollTime, setAutomaticScrollTime] = React.useState<number | undefined>(props.widgetsConfig.topReviews.automaticScrollTime !== undefined && props.widgetsConfig.topReviews.automaticScrollTime !== 0 ? props.widgetsConfig.topReviews.automaticScrollTime : reviewsConfig?.topReviews.maxAutomaticScrollTime);

    const searchReviews = async (filter: string) => {
        setIsSearching(true);
        try {
            await getReviews(dispatch, filter);
            setIsSearching(false);
        } catch(e) {
            console.error(e);
            setIsSearching(false);
        }
    };

    const createFiltersAndSearch = (searchInput: string) => {
        if (selectedDealerId) {
            const filters: string[] = [];
            if (searchInput.length > MIN_LETTERS_TO_SEARCH) {
                const containedFilter = searchContainedValues(ATTR_TO_SEARCH.map(attr => {return {key: attr, value: searchInput}}));
                filters.push(containedFilter);
            }
            currentFilters.forEach(currentFilter => {
                if (currentFilter.filters.length > 0) {
                    let newFilter = "";
                    if (currentFilter.attribute === "provider" || currentFilter.attribute === "rating") {
                        const stringFilters = [...currentFilter.filters];
                        newFilter = `c.${currentFilter.attribute} IN (${currentFilter.attribute === "provider" ? stringFilters.map(f => `"${f}"`).join(",") : stringFilters.join(",")})`;
                    } else if (currentFilter.attribute === "lastDays" && reviewsConfig) {
                        const lastDays = reviewsConfig.topReviews.filters.dates[currentFilter.filters[0]]?.numberOfDays;
                        if (lastDays && !isNaN(parseInt(lastDays))) {
                            const date = new Date();
                            date.setDate(date.getDate() - parseInt(lastDays));
                            newFilter = `(c["date"] >= "${date.toISOString()}")`;
                        }
                    } else if (currentFilter.attribute === "customDates" && currentFilter.filters.length === 2) {
                        const startDate = new Date(currentFilter.filters[0]);
                        const endDate = new Date(currentFilter.filters[1]);
                        newFilter = `(c["date"] >= "${startDate.toISOString()}" and c["date"] <= "${endDate.toISOString()}")`;
                    }
                    if (newFilter) filters.push(newFilter);
                }
            })
            if (filters.length) {
                filters.push(`c.dealerId = "${selectedDealerId}"`);
                searchReviews(`?filter=${filters.join(" and ")}`);
            }
        }
    }

    const debounceFunction = React.useCallback(
        debounce((_query: string, _filters?: T.ICustomFilter[]) => {
            setQueryFilters({query: _query, filters: JSON.stringify(_filters)});
        }, 400), []
    ); 

    const searchReviewsFromInput = (value: string) => {
        debounceFunction(value.trim());
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);
        if (value.length > MIN_LETTERS_TO_SEARCH || currentFilters.some(cf => cf.filters.length > 0)) {
            searchReviewsFromInput(value);
        } else {
            setCurrentReviews([...selectedReviews]);
        }
    }

    const handleClickOutside = (e: any) => {
        if (componentRef?.current) {
            const ref: (HTMLDivElement|null) = componentRef.current;
            if (!ref.contains(e.target)) props.onClose();
        }
    }

    const handleSelectReviews = (reviewSelected: Review) => {
        const selectedIndex = selectedReviews.findIndex(sr => sr.id === reviewSelected.id);
        if (selectedIndex >= 0) {
            setSelectedReviews([
                ...selectedReviews.slice(0, selectedIndex),
                ...selectedReviews.slice(selectedIndex + 1)
            ]);
        } else {
            setSelectedReviews([...selectedReviews, reviewSelected]);
        }
    }

    const saveReviews = async () => {
        if (!shouldNotSave && widgets) {
            setIsSaving(true);
            try {
                let newWidgets = cloneDeep(widgets);
                newWidgets[`${props.selectedTab}Config`].topReviews.reviews = selectedReviews;
                newWidgets[`${props.selectedTab}Config`].topReviews.automaticScroll = automaticScroll;
                newWidgets[`${props.selectedTab}Config`].topReviews.automaticScrollTime = automaticScrollTime;
                await saveWidgetsConfig(dispatch, newWidgets);
                setIsSaving(false);
                props.onClose();
            } catch(e) {
                setIsSaving(false);
                console.error(e);
            }
        }
    }

    const createChips = () => {
        let newChips: string[] = [];
        currentFilters.forEach(currentFilter => {
            currentFilter.filters.map(customFilter => {
                let newChip = "";
                if (currentFilter.attribute === "provider") {
                    newChip = capitalizeFirstLetter(customFilter);
                } else if (currentFilter.attribute === "rating") {
                    newChip = customFilter === '1' ? `1 star` : `${customFilter} stars`;
                } else if (currentFilter.attribute === "lastDays" && reviewsConfig) {
                    const lastDays = reviewsConfig.topReviews.filters.dates[customFilter];
                    if (lastDays) newChip = lastDays.displayName;
                }
                if (newChip) newChips.push(newChip);
            });
            if (currentFilter.attribute === "customDates" && currentFilter.filters.length === 2) {
                newChips.push(`${currentFilter.filters[0]} - ${currentFilter.filters[1]}`);
            }
        })
        setChips(newChips);
    }

    const handleRemoveChip = (index: number) => {
        const chipFound = chips[index];
        if (chipFound) {
            let newCurrentFilters = cloneDeep(currentFilters);
            let filterIndex = -1;
            let value = "";
            if (chipFound.indexOf('star') >= 0) {
                value = chipFound.split(" ")[0];
                filterIndex = newCurrentFilters.findIndex(cf => cf.attribute === "rating");
            } else if (chipFound.indexOf('ast') >= 0) {
                newCurrentFilters = newCurrentFilters.filter(cf => cf.attribute !== "lastDays");
            } else if (chipFound.indexOf('-') >= 0 && chipFound.indexOf('/') >= 0) {
                newCurrentFilters = newCurrentFilters.filter(cf => cf.attribute !== "customDates");
            } else {
                value = chipFound.toUpperCase();
                filterIndex = newCurrentFilters.findIndex(cf => cf.attribute === "provider");
            }
            if (filterIndex >= 0) {
                const customFilters = newCurrentFilters[filterIndex].filters.filter(f => f !== value);
                newCurrentFilters[filterIndex].filters = customFilters;
            }
            setCurrentFilters([...newCurrentFilters]);
        }
    }

    const handleRemoveAllChips = () => {
        setCurrentFilters([]);
    }

    const changeAutomaticScroll = (value) => {
        setAutomaticScroll(Boolean(value));
    }

    const changeAutomaticScrollTime = (id: string, value: number) => {
        setAutomaticScrollTime(Math.round(value));
    }

    React.useEffect(() => {
        if (queryFilters.query !== undefined) createFiltersAndSearch(queryFilters.query);
    }, [queryFilters]);

    React.useEffect(() => {
        if (widgets) {
            setAutomaticScroll(widgets[`${props.selectedTab}Config`].topReviews.automaticScroll);
        }
    }, [JSON.stringify(widgets)]);

    React.useEffect(() => {
        createChips();
        if (currentFilters.some(cf => cf.filters.length > 0)) {
            debounceFunction(inputValue.trim(), currentFilters);
        } else if (inputValue.length <= MIN_LETTERS_TO_SEARCH) {
            setCurrentReviews([...selectedReviews]);
        }
    }, [JSON.stringify(currentFilters)]);

    React.useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    React.useEffect(() => {
        if (reviews && (inputValue.length > MIN_LETTERS_TO_SEARCH || currentFilters.some(cf => cf.filters.length > 0))) {
            setCurrentReviews(reviews);
        }
    }, [reviews]);

    return (
        <S.Root
            ref={componentRef}
            style={props.style}
            posX={props.posX}
            posY={props.posY}
        >
            {
                openFilters &&
                <ReviewFilters
                    setOpenFilers={setOpenFilters}
                    currentFilters={currentFilters}
                    setCurrentFilters={setCurrentFilters}
                />
            }
            <S.TopContent>
                <S.BoxTitle>
                    Search for reviews
                    {   
                        reviewsConfig &&
                        <S.BoxWarning
                            colorValue={shouldNotSave ? "#b7332b" : undefined}
                        >
                            {`Please select ${reviewsConfig.topReviews.minReviews}-${reviewsConfig.topReviews.maxReviews} reviews.`}
                        </S.BoxWarning>
                    }
                </S.BoxTitle>
                <S.InputContainer>
                    <S.LoopIconContainer>
                        <LoopIcon />
                    </S.LoopIconContainer>
                    <S.SearchInput
                        placeholder={`Search reviews`}
                        value={inputValue}
                        onChange={handleInputChange}
                    />
                    <S.FilterButton>
                        <TCButton
                            type={TC_BUTTON_TYPE.LISMA}
                            onClick={() => {setOpenFilters(true)}}
                        >
                            Filter
                        </TCButton>
                    </S.FilterButton>
                </S.InputContainer>
            </S.TopContent>
            <S.ChipsContainer
                displayValue={currentFilters.some(cf => cf.filters.length > 0) ? 'auto' : 'none'}
            >
                <Chips
                    items={chips.slice(0, CHIPS_NUMBER)}
                    handleRemove={handleRemoveChip}
                    style={{width: 'auto'}}
                    chipStyle={{marginTop: '0px'}}
                />
                <MoreChips
                    chips={chips.slice(CHIPS_NUMBER, chips.length)}
                    handleRemove={handleRemoveChip}
                    handleRemoveAlll={handleRemoveAllChips}
                    nchips={CHIPS_NUMBER}
                />
            </S.ChipsContainer>
            <S.MiddleContent>
                {
                    currrentReviews.map((review, index) => {
                        return (
                            <S.ReviewCardContainer key={index} >
                                <ReviewCard
                                    review={review}
                                    dateLocation={"left"}
                                    selected={selectedReviews.some(r => r.id == review.id)}
                                    handleSelectReviews={handleSelectReviews}
                                    showFeatureCheck={true}
                                />
                            </S.ReviewCardContainer>
                        );
                    })
                }
            </S.MiddleContent>
            <S.Divider />
            <S.BottomContent>
                <S.DragContainer>
                    <S.BoxTitle style={{padding: "0px 15px"}} >
                        Drag to order Reviews
                    </S.BoxTitle>
                    <DragReviews
                        reviews={selectedReviews}
                        setReviews={setSelectedReviews}
                        style={{marginTop: "20px"}}
                    />
                </S.DragContainer>
                <S.ActionsContainer>
                    <S.AutomaticScroll>
                        <S.AutomaticScrollContainer>
                            <S.AutomaticScrollTitle>
                                Automatic Scroll
                            </S.AutomaticScrollTitle>
                            <Switch checked={automaticScroll} onChange={changeAutomaticScroll}/>
                        </S.AutomaticScrollContainer>
                        {
                            reviewsConfig &&
                            <Slider
                                id="automatic-scroll-time"
                                title='Scroll Speed'
                                tip='sec'
                                value={automaticScrollTime ?? reviewsConfig.topReviews.maxAutomaticScrollTime}
                                handleValue={changeAutomaticScrollTime}
                                minValue={reviewsConfig.topReviews.minAutomaticScrollTime}
                                maxValue={reviewsConfig.topReviews.maxAutomaticScrollTime}
                                disabled={!(automaticScroll)}
                            />
                        }
                    </S.AutomaticScroll>
                    <S.ButtonsContainer>
                        <TCButton
                            type={TC_BUTTON_TYPE.LISMA}
                            onClick={() => props.onClose()}
                            disabled={isSearching || isSaving}
                        >
                            Cancel
                        </TCButton>
                        <TCButton
                            type={TC_BUTTON_TYPE.LISMA}
                            onClick={saveReviews}
                            disabled={isSearching || isSaving || shouldNotSave}
                        >
                            Save
                        </TCButton>
                    </S.ButtonsContainer>
                </S.ActionsContainer>
            </S.BottomContent>
        </S.Root>
    );
}

export default SearchReviews;
